import { useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { BrandedQRCode } from "@/components/StyledQRCode";
import { Button } from "@/components/ui/button";
import { useQRGenerator } from "@/hooks/embed/useQRGenerator";
import type { Agent } from "@/types/agent/agent";
import {
  DEFAULT_QR_DISPLAY_CONFIG,
  type QRDisplayConfig,
  type QRDownloadFormat,
} from "@/types/ui/branding";

interface PageQRCodeCardProps {
  genie: Agent;
  /** The full public page URL (`…/help/:slug`). */
  url: string;
}

/**
 * A QR code that opens the Genie Page itself.
 *
 * Every other QR in the product opens the conversation directly
 * (`/live-agent/:slug`) — right for a "talk to us" sticker, wrong for a
 * counter card or poster that should offer *every* way in: the Genie, the
 * phone number, the guides, the manuals and the app. This one points a scan
 * at the whole support page.
 */
export const PageQRCodeCard = ({ genie, url }: PageQRCodeCardProps) => {
  const [isDownloading, setIsDownloading] = useState<QRDownloadFormat | null>(null);

  const displayConfig: QRDisplayConfig = {
    ...DEFAULT_QR_DISPLAY_CONFIG,
    foregroundColor: genie.branding?.qrCodeStyle?.foregroundColor || "#000000",
    backgroundColor: genie.branding?.qrCodeStyle?.backgroundColor || "#FFFFFF",
  };

  const { downloadQR } = useQRGenerator(url, genie, { size: 400 });

  const handleDownload = async (format: QRDownloadFormat) => {
    setIsDownloading(format);
    try {
      await downloadQR(format, displayConfig);
    } catch {
      toast.error("Couldn't download the QR code — please try again.");
    } finally {
      setIsDownloading(null);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-6">
      <div className="shrink-0">
        <BrandedQRCode
          value={url}
          size={140}
          style={{
            foregroundColor: displayConfig.foregroundColor,
            backgroundColor: displayConfig.backgroundColor,
            cornerStyle: genie.branding?.qrCodeStyle?.cornerStyle || "square",
            logoUrl: genie.branding?.logoUrl,
            logoSize: genie.branding?.qrCodeStyle?.logoSize || 50,
          }}
          genie={genie}
          displayConfig={displayConfig}
        />
      </div>

      <div className="flex flex-col gap-3 text-center sm:text-left">
        <p className="text-[13.5px] leading-snug text-gray-600">
          Scanning opens this page — every way to reach you on one screen. Put it
          on counters, menus, posters and packaging; the link never changes, so
          printed codes keep working as the page grows.
        </p>
        <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
          <Button
            variant="outline"
            className="gap-1.5"
            disabled={isDownloading !== null}
            onClick={() => void handleDownload("png")}
          >
            <Download className="h-4 w-4" />
            {isDownloading === "png" ? "Preparing…" : "Download PNG"}
          </Button>
          <Button
            variant="outline"
            className="gap-1.5"
            disabled={isDownloading !== null}
            onClick={() => void handleDownload("svg")}
          >
            <Download className="h-4 w-4" />
            {isDownloading === "svg" ? "Preparing…" : "Download SVG"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PageQRCodeCard;
